import React from 'react';
import { useFormContext, useController } from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import { Checkbox, CheckboxProps } from '@epoch-ui/components/Checkbox';

import {
  FormField,
  FormControl,
  FormDescription,
  FormLabel,
  FormItem,
  FormMessage
} from '../../form';

const CheckboxGroupFieldContext = React.createContext<{
  fieldName: string;
  disabled?: boolean;
} | null>(null);

const useCheckboxGroupField = (): {
  fieldName: string;
  disabled?: boolean;
} => {
  const context = React.useContext(CheckboxGroupFieldContext);
  if (!context) {
    throw new Error('useCheckboxGroupField must be used within a CheckboxGroupFieldContext');
  }
  return context;
};

export interface CheckboxGroupFieldProps {
  name: string;
  label?: string | React.ReactNode;
  description?: string | React.ReactNode;
  className?: string;
  disabled?: boolean;
  children: React.ReactNode;
}

const CheckboxGroupFieldRoot: React.FC<CheckboxGroupFieldProps> = ({
  name,
  label,
  description,
  className,
  disabled,
  children
}) => {
  const { control } = useFormContext();

  const contextValue = React.useMemo(
    () => ({
      fieldName: name,
      disabled
    }),
    [name, disabled]
  );

  return (
    <CheckboxGroupFieldContext.Provider value={contextValue}>
      <FormField
        control={control}
        name={name}
        render={(): React.ReactElement => (
          <FormItem className={className}>
            {label && <FormLabel id={`${name}-label`}>{label}</FormLabel>}
            {description && <FormDescription>{description}</FormDescription>}
            <FormControl>
              <div
                className="tw-flex tw-flex-col tw-gap-2"
                role="group"
                aria-labelledby={label ? `${name}-label` : undefined}
              >
                {children}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </CheckboxGroupFieldContext.Provider>
  );
};

// =============================================================================
// CHECKBOX GROUP ITEM COMPONENT
// =============================================================================

export interface CheckboxGroupItemProps extends Omit<CheckboxProps, 'checked' | 'onCheckedChange'> {
  value: string;
  children: React.ReactNode;
}

const Item = React.forwardRef<HTMLButtonElement, CheckboxGroupItemProps>(
  ({ value, children, className, disabled: itemDisabled, ...props }, ref): React.ReactElement => {
    const { fieldName, disabled: groupDisabled } = useCheckboxGroupField();
    const { field, fieldState } = useController({ name: fieldName });

    const currentValues: string[] = Array.isArray(field.value) ? field.value : [];
    const isChecked = currentValues.includes(value);
    const isDisabled = itemDisabled || groupDisabled;

    const handleCheckedChange = React.useCallback(
      (checked: boolean | 'indeterminate'): void => {
        if (checked === 'indeterminate') return;

        const newValues = checked
          ? [...currentValues, value]
          : currentValues.filter((v) => v !== value);

        field.onChange(newValues);
      },
      [currentValues, value, field]
    );

    return (
      <FormLabel
        className={cn(
          'tw-flex tw-items-center tw-gap-2 tw-font-normal',
          isDisabled && 'tw-cursor-not-allowed tw-opacity-50',
          className
        )}
      >
        <Checkbox
          ref={ref}
          checked={isChecked}
          onCheckedChange={handleCheckedChange}
          disabled={isDisabled}
          error={!!fieldState.error && currentValues.length === 0}
          {...props}
        />
        <span className="tw-text-sm">{children}</span>
      </FormLabel>
    );
  }
);

Item.displayName = 'CheckboxGroupField.Item';

// =============================================================================
// COMPOUND COMPONENT EXPORT
// =============================================================================

const CheckboxGroupField = Object.assign(CheckboxGroupFieldRoot, {
  Item
});

export { CheckboxGroupField };
